/*
  PortfolioSwitcher のドラッグ結果を full list の順序へ変換する。
  hidden portfolio を保ったまま moved item だけを移動し、API へ送る neighbor 情報も返せるようにする。
*/
import type { Portfolio } from '@/types'

export type PortfolioMove = {
  movedPortfolioUuid: string
  previousPortfolioUuid?: string
  nextPortfolioUuid?: string
}

/**
 * 表示中 subset のドラッグ結果を、残り要素の相対順を保つ full list move に変換する。
 */
export const reorderPortfoliosByDrag = (params: {
  portfolios: readonly Portfolio[]
  visiblePortfolios: readonly Portfolio[]
  activePortfolioUuid: string
  overPortfolioUuid: string
}): readonly Portfolio[] => {
  const {
    portfolios,
    visiblePortfolios,
    activePortfolioUuid,
    overPortfolioUuid,
  } = params
  const activeVisibleIndex = visiblePortfolios.findIndex(
    (portfolio) => portfolio.uuid === activePortfolioUuid,
  )
  const overVisibleIndex = visiblePortfolios.findIndex(
    (portfolio) => portfolio.uuid === overPortfolioUuid,
  )
  if (
    activeVisibleIndex === -1 ||
    overVisibleIndex === -1 ||
    activeVisibleIndex === overVisibleIndex
  ) {
    return portfolios
  }

  const activePortfolio = portfolios.find(
    (portfolio) => portfolio.uuid === activePortfolioUuid,
  )
  if (activePortfolio === undefined) {
    return portfolios
  }

  const portfoliosWithoutActive = portfolios.filter(
    (portfolio) => portfolio.uuid !== activePortfolioUuid,
  )
  const overIndexWithoutActive = portfoliosWithoutActive.findIndex(
    (portfolio) => portfolio.uuid === overPortfolioUuid,
  )
  if (overIndexWithoutActive === -1) {
    return portfolios
  }

  const shouldInsertAfterOver = activeVisibleIndex < overVisibleIndex
  const insertIndex = shouldInsertAfterOver
    ? overIndexWithoutActive + 1
    : overIndexWithoutActive

  return [
    ...portfoliosWithoutActive.slice(0, insertIndex),
    activePortfolio,
    ...portfoliosWithoutActive.slice(insertIndex),
  ]
}

/**
 * 並び替え後の full list から moved item の前後 neighbor を返す。
 */
export const findPortfolioMove = (params: {
  movedPortfolioUuid: string
  portfolios: readonly Portfolio[]
}): PortfolioMove => {
  const { movedPortfolioUuid, portfolios } = params
  const movedIndex = portfolios.findIndex(
    (portfolio) => portfolio.uuid === movedPortfolioUuid,
  )
  if (movedIndex === -1) {
    throw new Error('Moved portfolio is not found.')
  }

  return {
    movedPortfolioUuid,
    previousPortfolioUuid:
      movedIndex > 0 ? portfolios[movedIndex - 1].uuid : undefined,
    nextPortfolioUuid:
      movedIndex < portfolios.length - 1
        ? portfolios[movedIndex + 1].uuid
        : undefined,
  }
}
